import Form from './components/Form';
// @ts-ignore
import Table from '../../components/Table';
import {
  ascending,
  FPA,
  txHistoryTableHeading,
  vestingOptions,
} from '../../utils/constants';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MarketContextType,
  MarketType,
  TxType,
} from '../../contexts/MarketsContext/provider.interface';
import useMarkets from '../../contexts/MarketsContext/useMarkets';
import { timestampToDate } from '../../utils/timestampToDate';
import arrow from '../../assets/icons/arrow-left.svg';
import { collapseString } from '../../utils/collapseString';
import { getMarketData, getMarketTxs, getProtocolFee } from './exchange.service';
import { getTokenByAddress } from '../../utils/getTokenByAddress';
import { BigNumber, ethers, utils } from 'ethers';
import MarketChart from './components/Chart';
import { formatDecimals } from '../../utils/formatDecimals';
import { Loader } from '@airdao/ui-library';
import Pagination from '../../components/Pagination';
import SpendRewards from './components/SpendRewards';
// @ts-ignore
import { ContractNames } from '@airdao/airdao-bond';
import useBondContracts from '../../contexts/BondContractContext/useBondContracts';

const itemsPerPage = 10;

const Exchange = () => {
  const { id } = useParams();
  const contracts = useBondContracts();
  const { tokens, isLoading }: MarketContextType = useMarkets();

  const [txs, setTxs] = useState<TxType[]>([]);
  const [marketData, setMarketData] = useState<MarketType | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [protocolFee, setProtocolFee] = useState(0);

  useEffect(() => {
    if (id) {
      getMarketTxs(id?.toString()).then(res => setTxs(res.data));
      getMarketData(id).then(res => setMarketData(res.data));
    }
  }, [id]);

  const payoutToken = useMemo(() => {
    return getTokenByAddress(marketData?.payoutToken, tokens);
  }, [tokens, marketData]);

  const quoteToken = useMemo(() => {
    return getTokenByAddress(marketData?.quoteToken, tokens);
  }, [tokens, marketData]);

  const txTableData = useMemo(() => {
    if (!marketData) return [];

    return txs.sort((a: any, b: any) => b.date - a.date).map((el) => {
      return [
        timestampToDate(new Date(el.date).getTime()),
        `${formatDecimals(utils.formatUnits(BigNumber.from(el?.bondAmount), quoteToken?.decimals), quoteToken?.contractAddress, tokens)} ${quoteToken?.symbol.toUpperCase()}`,
        `${formatDecimals(utils.formatUnits(BigNumber.from(el?.payoutAmount), payoutToken?.decimals), payoutToken?.contractAddress, tokens)} ${payoutToken?.symbol.toUpperCase()}`,
        <span style={{color: el?.discount > 0 ? 'green' : 'red'}}>
          {el?.discount ? el?.discount.toFixed(2) + '%' : '0.00'}
        </span>,
        <a href={`https://airdao.io/explorer/tx/${el.txHash}/`} className="link" target="_blank">
          {collapseString(el.txHash)}
        </a>,
        <a href={`https://airdao.io/explorer/address/${el.addressFrom}/`} className="link" target="_blank">
          {collapseString(el.addressFrom)}
        </a>,
      ]
    })
  }, [txs, marketData]);

  useEffect(() => {
    if (marketData) {
      getProtocolFee().then((res) => {
        const { address } = contracts.getContractByName(
          marketData.vestingType === 'Fixed-term'
            ? ContractNames.FixedTermTeller
            : ContractNames.FixedExpiryTeller
        )
        if (res.data) {
          setProtocolFee(+res.data.find((el: any) => el.address === address).fee / 1000)
        }
      });

    }
  }, [marketData]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return txTableData.slice(startIndex, endIndex);
  }, [txTableData, currentPage]);

  const notStarted =
    marketData && marketData?.start > new Date().getTime() / 1000;

  const status = useMemo(() => {
    if (!marketData) {
      return '';
    } else if (notStarted) {
      return 'Not started';
    } else if (marketData?.isLive) {
      return 'Active';
    } else {
      return 'Closed';
    }
  }, [marketData]);

  const assetValue = !!marketData?.capacity && ((+utils.formatUnits(BigNumber.from(marketData?.capacity), quoteToken?.decimals)) * (payoutToken?.price || 0)).toFixed(2);
  const totalPages = Math.ceil(txTableData.length / itemsPerPage);

  if (!marketData) {
    return <Loader />;
  }
  return (
    <div>
      <Link to="/" className="exchange-back">
        <img src={arrow} alt="arrow left" />
        Back
      </Link>
      <div className="exchange-wrapper">
        <div className="exchange-heading">
          {marketData && (
            <div className="exchange-title">
              <img
                src={
                  getTokenByAddress(marketData?.payoutToken, tokens)?.logoUrl
                }
                alt="logo"
              />
              <span className="exchange-title__text">
                {getTokenByAddress(
                  marketData?.payoutToken,
                  tokens,
                )?.symbol.toUpperCase()}
              </span>
            </div>
          )}
          {marketData && (
            <div className="exchange-info">
              <div className="exchange-info__item">
                <span className="exchange-info__label">ASSETS IN BOND</span>
                <span className="exchange-info__value">
                  <img src={payoutToken?.logoUrl} alt="logo" />
                  {payoutToken?.name}
                </span>
              </div>
              <div className="exchange-info__item">
                <span className="exchange-info__label">ASSET VALUE</span>
                <span className="exchange-info__value">
                  ${marketData.isLive ? assetValue : 0}
                </span>
              </div>
              {marketData?.vestingType === 'Fixed-expiry' ? (
                <div className="exchange-info__item">
                  <span className="exchange-info__label">
                    VESTING EXPIRED DATE
                  </span>
                  <span className="exchange-info__value">
                    {timestampToDate(+marketData.vesting)}
                  </span>
                </div>
              ) : (
                <div className="exchange-info__item">
                  <span className="exchange-info__label">LOCK PERIOD</span>
                  <span className="exchange-info__value">
                    {!!marketData?.vesting &&
                      vestingOptions.find(
                        el => el.value === +marketData.vesting,
                      )?.label}
                  </span>
                </div>
              )}
              <div className="exchange-info__item">
                <span className="exchange-info__label">AVAILABLE AMOUNT</span>
                <span className="exchange-info__value">
                  {!!marketData?.maxPayout &&
                    formatDecimals(
                      utils.formatUnits(
                        marketData.capacity,
                        payoutToken?.decimals,
                      ),
                      payoutToken?.contractAddress,
                      tokens,
                    )}{' '}
                  {payoutToken?.symbol}
                </span>
              </div>
              <div className="exchange-info__item">
                <span className="exchange-info__label">MARKET DURATION</span>
                <span className="exchange-info__value">
                  {timestampToDate(marketData.start, true)} -{' '}
                  {timestampToDate(marketData.conclusion, true)}
                </span>
              </div>
              <div className="exchange-info__item">
                <span className="exchange-info__label">STATUS</span>
                <span
                  className={`exchange-info__value exchange-info__status ${
                    marketData.isLive && !notStarted
                      ? 'exchange-info__status_active'
                      : ''
                  }`}
                >
                  {status}
                </span>
              </div>
            </div>
          )}
          {marketData && quoteToken?.price && (
            <MarketChart
              tokenAddress={marketData.payoutToken}
              fpaPrice={
                marketData.marketType === FPA ? marketData.askingPrice : null
              }
              marketId={marketData.id}
              marketStart={marketData.start}
              quotePrice={quoteToken?.price}
              askingPrice={marketData.askingPrice}
            />
          )}
        </div>
        {marketData && (
          <div>
            <Form marketData={marketData} protocolFee={protocolFee} />
            {quoteToken?.contractAddress === ethers.constants.AddressZero && (
              <SpendRewards marketData={marketData} protocolFee={protocolFee}/>
            )}
          </div>
        )}
      </div>
      <h2 className="tx-history-title">Transaction history</h2>
      <Table
        headingCells={txHistoryTableHeading}
        rowCells={paginatedData}
        gridTemplateClassName="tx-history"
        isLoading={isLoading.txs || isLoading.tokens}
      />
      {!!txTableData.length && itemsPerPage > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      )}
    </div>
  );
};

export default Exchange;
